import type { User, Role, Permission } from '../types';

/** Get the display slug for the user's first role */
export function getUserRoleSlug(user: { roles?: Array<{ slug?: string } | string> } | null | undefined): string {
  if (!user?.roles?.length) return 'team_member';
  const role = user.roles[0];
  return (typeof role === 'object' ? role?.slug : role) || 'team_member';
}

/** Get the display name for the user's first role */
export function getUserRoleName(user: { roles?: Array<{ name?: string } | string> } | null | undefined): string {
  if (!user?.roles?.length) return 'Team Member';
  const role = user.roles[0];
  return (typeof role === 'object' ? role?.name : null) || 'Team Member';
}

/**
 * RBAC v2 Permission Utilities
 *
 * Handles multi-role permission resolution for users with roles: Role[].
 * Each role has permissions[] and an optional superPermission level.
 *
 * Super-permission hierarchy: * > write > read > list
 * Permission format: "resource.action" strings (e.g. "projects.list", "pods.write")
 */

// Super-permission hierarchy — higher index = more powerful
const SUPER_PERMISSION_HIERARCHY: readonly string[] = ['list', 'read', 'write', '*'];

/**
 * Map of super-permission levels to the action strings they implicitly grant.
 * Example: 'write' implies 'read' and 'list' as well.
 */
const SUPER_PERMISSION_IMPLIES: Record<string, readonly string[]> = {
  '*': ['*', 'write', 'read', 'list', 'view', 'create', 'edit', 'delete', 'manage', 'assign', 'approve', 'export', 'configure', 'browse'],
  write: ['write', 'read', 'list', 'view', 'create', 'edit', 'browse'],
  read: ['read', 'list', 'view', 'browse'],
  list: ['list', 'browse'],
};

/**
 * Check whether a super-permission level satisfies a requested action.
 */
function superPermissionSatisfies(superPerm: string, requestedAction: string): boolean {
  const implied = SUPER_PERMISSION_IMPLIES[superPerm];
  if (!implied) return false;
  return implied.includes(requestedAction);
}

/**
 * Normalise a Role-like value: accepts either a full Role object or a plain
 * object with at minimum `permissions` and optional `superPermission`.
 */
function normalizeRole(role: unknown): Role | null {
  if (!role || typeof role !== 'object') return null;
  const r = role as Record<string, unknown>;
  // Must at least look like a role object (has slug or permissions array)
  if (Array.isArray(r.permissions) || typeof r.slug === 'string') {
    return role as Role;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Core permission check functions
// ---------------------------------------------------------------------------

/**
 * Get all effective permissions from all of a user's roles.
 * Merges permissions from every role and expands super-permissions into
 * concrete permission entries where possible.
 */
export function getUserPermissions(user: User | null | undefined): Permission[] {
  if (!user) return [];

  // If user already has a flat permissions array (populated by backend), use it
  if (user.permissions && user.permissions.length > 0) {
    return user.permissions;
  }

  // Otherwise derive from roles
  const roles = user.roles;
  if (!roles || roles.length === 0) return [];

  const allPermissions: Permission[] = [];

  for (const rawRole of roles) {
    const role = normalizeRole(rawRole);
    if (!role) continue;

    // A role with superPermission '*' grants everything
    if (role.superPermission === '*') {
      return [{ resource: '*', action: '*', scope: 'global' }];
    }

    // Add explicit permissions from this role
    if (role.permissions) {
      allPermissions.push(...role.permissions);
    }
  }

  return deduplicatePermissions(allPermissions);
}

/**
 * Check if a user has a specific permission.
 *
 * Supports two calling styles:
 *   hasPermission(user, 'projects.list')          — dot-notation string
 *   hasPermission(user, 'projects', 'list')        — resource + action separate
 *   hasPermission(user, 'projects', 'list', 'global') — with scope
 */
export function hasPermission(
  user: User | null | undefined,
  resourceOrDotString: string,
  action?: string,
  scope?: string
): boolean {
  if (!user) return false;

  // Parse dot-notation if action is not provided
  let resource: string;
  let resolvedAction: string;
  if (!action && resourceOrDotString.includes('.')) {
    const parts = resourceOrDotString.split('.');
    resource = parts[0];
    resolvedAction = parts.slice(1).join('.');
  } else {
    resource = resourceOrDotString;
    resolvedAction = action || '*';
  }

  // Check via roles first (RBAC v2 path)
  if (user.roles && user.roles.length > 0) {
    for (const rawRole of user.roles) {
      const role = normalizeRole(rawRole);
      if (!role) continue;

      // Super-permission '*' grants everything unconditionally
      if (role.superPermission === '*') {
        return true;
      }

      // Other super-permissions cover a defined set of actions
      if (role.superPermission && superPermissionSatisfies(role.superPermission, resolvedAction)) {
        return true;
      }

      // Check explicit permissions on the role
      if (role.permissions) {
        const match = role.permissions.some(p => {
          const resourceMatch = p.resource === resource || p.resource === '*';
          const actionMatch = p.action === resolvedAction || p.action === '*';
          const scopeMatch = !scope || p.scope === scope || p.scope === 'global';
          return resourceMatch && actionMatch && scopeMatch;
        });
        if (match) return true;
      }
    }
  }

  // Fallback: check the backend-computed flat permissions array (user.permissions)
  const permissions = user.permissions || [];
  if (permissions.length > 0) {
    return permissions.some(p => {
      const resourceMatch = p.resource === resource || p.resource === '*';
      const actionMatch = p.action === resolvedAction || p.action === '*';
      const scopeMatch = !scope || p.scope === scope || p.scope === 'global';
      return resourceMatch && actionMatch && scopeMatch;
    });
  }

  return false;
}

/**
 * Check if user has ANY of the listed permissions.
 */
export function hasAnyPermission(
  user: User | null | undefined,
  permissions: string[]
): boolean {
  return permissions.some(p => hasPermission(user, p));
}

/**
 * Check if user has ALL of the listed permissions.
 */
export function hasAllPermissions(
  user: User | null | undefined,
  permissions: string[]
): boolean {
  return permissions.every(p => hasPermission(user, p));
}

/**
 * Check if user holds a role with a given slug.
 * Resolved solely from the populated `roles[]` array — the RBAC v1 `user.role`
 * string was removed and is never consulted.
 */
export function hasRoleSlug(user: User | null | undefined, slug: string): boolean {
  if (!user) return false;

  // Check multi-role array
  if (user.roles && user.roles.length > 0) {
    return user.roles.some(r => {
      const role = normalizeRole(r);
      return role?.slug === slug;
    });
  }

  return false;
}

/**
 * Check if user is an admin, i.e. holds any role with superPermission '*'.
 * Admin-ness is not derived from a role slug, and the removed RBAC v1
 * `user.role` string is never consulted.
 */
export function isAdmin(user: User | null | undefined): boolean {
  if (!user) return false;

  // Check multi-role for superPermission '*'
  if (user.roles && user.roles.length > 0) {
    return user.roles.some(r => {
      const role = normalizeRole(r);
      return role?.superPermission === '*';
    });
  }

  return false;
}

/**
 * Get all role slugs for the user, derived solely from the populated `roles[]`
 * array (unpopulated id strings are skipped). There is no fallback to the
 * removed RBAC v1 `user.role` string — a user without roles has no slugs.
 */
export function getUserRoleSlugs(user: User | null | undefined): string[] {
  if (!user) return [];
  if (user.roles && user.roles.length > 0) {
    return user.roles
      .map(r => normalizeRole(r))
      .filter((r): r is Role => r !== null)
      .map(r => r.slug);
  }
  return [];
}

/**
 * Get the highest super-permission level across all of the user's roles.
 * Returns undefined if no roles have super-permissions (including users with no
 * roles at all).
 */
export function getHighestSuperPermission(user: User | null | undefined): string | undefined {
  if (!user?.roles || user.roles.length === 0) {
    return undefined;
  }

  let highestIndex = -1;
  for (const rawRole of user.roles) {
    const role = normalizeRole(rawRole);
    if (!role?.superPermission) continue;
    const idx = SUPER_PERMISSION_HIERARCHY.indexOf(role.superPermission);
    if (idx > highestIndex) highestIndex = idx;
  }

  return highestIndex >= 0 ? SUPER_PERMISSION_HIERARCHY[highestIndex] : undefined;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function deduplicatePermissions(permissions: Permission[]): Permission[] {
  const seen = new Set<string>();
  const result: Permission[] = [];

  for (const p of permissions) {
    const key = `${p.resource}:${p.action}:${p.scope}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(p);
    }
  }

  return result;
}

// Re-export the hierarchy constant for consumers that need it
export { SUPER_PERMISSION_HIERARCHY };
